import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { gekkoApi } from '../../api/GekkoApi';
import {
  SoapNote,
  CreateSoapNoteRequest,
  UpdateSoapNoteRequest,
  SoapNoteListResponse,
} from '../../types/SoapNote.type';

interface SoapNotesContextState {
  // Current SOAP notes for the active case
  soapNotes: SoapNote[];
  activeSoapNote: SoapNote | null;
  isLoading: boolean;
  isGenerating: boolean;
  error: string | null;

  // Actions
  fetchSoapNotes: (caseId: string) => Promise<SoapNote[]>;
  generateSoapNote: (
    caseId: string,
    options?: CreateSoapNoteRequest
  ) => Promise<SoapNote>;
  getSoapNoteById: (caseId: string, noteId: string) => Promise<SoapNote>;
  updateSoapNote: (
    caseId: string,
    noteId: string,
    data: UpdateSoapNoteRequest
  ) => Promise<SoapNote>;
  deleteSoapNote: (caseId: string, noteId: string) => Promise<void>;
  setActiveSoapNote: (note: SoapNote | null) => void;
  clearSoapNotes: () => void;
}

const SoapNotesContext = createContext<SoapNotesContextState | undefined>(
  undefined
);

interface SoapNotesProviderProps {
  children: ReactNode;
}

export const SoapNotesProvider: React.FC<SoapNotesProviderProps> = ({
  children,
}) => {
  const [soapNotes, setSoapNotes] = useState<SoapNote[]>([]);
  const [activeSoapNote, setActiveSoapNote] = useState<SoapNote | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSoapNotes = useCallback(
    async (caseId: string): Promise<SoapNote[]> => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await gekkoApi.get<SoapNoteListResponse>(
          `/cases/${caseId}/soap-notes`
        );
        const notes = response.items || [];
        setSoapNotes(notes);
        // Set the most recent one as active if available
        if (notes.length > 0 && !activeSoapNote) {
          setActiveSoapNote(notes[0]);
        }
        return notes;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to fetch SOAP notes';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [activeSoapNote]
  );

  const generateSoapNote = useCallback(
    async (
      caseId: string,
      options?: CreateSoapNoteRequest
    ): Promise<SoapNote> => {
      setIsGenerating(true);
      setError(null);
      try {
        const response = await gekkoApi.post<SoapNote>(
          `/cases/${caseId}/soap-notes`,
          {
            body: options || {},
          }
        );
        // Add the new note to the list and set it as active
        setSoapNotes((prev) => [response, ...prev]);
        setActiveSoapNote(response);
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to generate SOAP notes';
        setError(message);
        throw err;
      } finally {
        setIsGenerating(false);
      }
    },
    []
  );

  const getSoapNoteById = useCallback(
    async (caseId: string, noteId: string): Promise<SoapNote> => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await gekkoApi.get<SoapNote>(
          `/cases/${caseId}/soap-notes/${noteId}`
        );
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to fetch SOAP note';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const updateSoapNote = useCallback(
    async (
      caseId: string,
      noteId: string,
      data: UpdateSoapNoteRequest
    ): Promise<SoapNote> => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await gekkoApi.put<SoapNote>(
          `/cases/${caseId}/soap-notes/${noteId}`,
          {
            body: data,
          }
        );
        // Update the note in the list
        setSoapNotes((prev) =>
          prev.map((note) => (note.id === noteId ? response : note))
        );
        if (activeSoapNote?.id === noteId) {
          setActiveSoapNote(response);
        }
        return response;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to update SOAP note';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [activeSoapNote]
  );

  const deleteSoapNote = useCallback(
    async (caseId: string, noteId: string): Promise<void> => {
      setIsLoading(true);
      setError(null);
      try {
        await gekkoApi.delete(`/cases/${caseId}/soap-notes/${noteId}`);
        // Remove the note from the list
        setSoapNotes((prev) => prev.filter((note) => note.id !== noteId));
        if (activeSoapNote?.id === noteId) {
          setActiveSoapNote(null);
        }
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to delete SOAP note';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [activeSoapNote]
  );

  const clearSoapNotes = useCallback(() => {
    setSoapNotes([]);
    setActiveSoapNote(null);
    setError(null);
  }, []);

  return (
    <SoapNotesContext.Provider
      value={{
        soapNotes,
        activeSoapNote,
        isLoading,
        isGenerating,
        error,
        fetchSoapNotes,
        generateSoapNote,
        getSoapNoteById,
        updateSoapNote,
        deleteSoapNote,
        setActiveSoapNote,
        clearSoapNotes,
      }}
    >
      {children}
    </SoapNotesContext.Provider>
  );
};

export const useSoapNotes = (): SoapNotesContextState => {
  const context = useContext(SoapNotesContext);
  if (context === undefined) {
    throw new Error('useSoapNotes must be used within a SoapNotesProvider');
  }
  return context;
};
